import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

const Slideshow = ({ slides = [], interval = 4500, heightClass = "h-[260px] sm:h-[420px] md:h-[560px]" }) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = slides.length;

  const go = useCallback((dir) => setIndex((i) => (i + dir + count) % count), [count]);
  const to = (i) => setIndex(i);

  useEffect(() => {
    if (paused || count <= 1) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % count), interval);
    return () => clearInterval(id);
  }, [paused, count, interval]);

  if (count === 0) return null;

  return (
    <div
      className={`relative w-full overflow-hidden border border-white/10 bg-black ${heightClass}`}
      data-testid="range-slideshow"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <AnimatePresence initial={false} mode="popLayout">
        <motion.img
          key={index}
          src={slides[index].src}
          alt={slides[index].alt || ""}
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1.02 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0 w-full h-full object-cover"
        />
      </AnimatePresence>

      <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/70 via-transparent to-[#050505]/20 pointer-events-none" />

      {count > 1 && (
        <>
          <button
            onClick={() => go(-1)}
            data-testid="slideshow-prev"
            aria-label="Vorheriges Bild"
            className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 z-10 w-11 h-11 flex items-center justify-center bg-[#050505]/60 border border-white/20 text-white hover:bg-[#ff0033] hover:border-[#ff0033] transition-colors backdrop-blur-sm"
          >
            <ChevronLeft size={22} />
          </button>
          <button
            onClick={() => go(1)}
            data-testid="slideshow-next"
            aria-label="Nächstes Bild"
            className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 z-10 w-11 h-11 flex items-center justify-center bg-[#050505]/60 border border-white/20 text-white hover:bg-[#ff0033] hover:border-[#ff0033] transition-colors backdrop-blur-sm"
          >
            <ChevronRight size={22} />
          </button>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => to(i)}
                data-testid={`slideshow-dot-${i}`}
                aria-label={`Bild ${i + 1}`}
                className={`h-2 rounded-full transition-all ${
                  i === index ? "w-8 bg-[#ff0033]" : "w-2 bg-white/40 hover:bg-white/70"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Slideshow;
